import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaTv, FaCircle, FaSyncAlt, FaPlay } from 'react-icons/fa';
import { isTvRuntime } from '../../services/capability';
import { startCastReceiver } from '../../services/cast';
import { dispatchNavKey } from '../../services/dpad';
import { dispatchRemotePointer } from '../../services/pointer';
import { savePlayerId } from '../../services/players';

// ---------------------------------------------------------------------------
// TV-side cast receiver (mounted once in App).
//
// On TV runtimes it:
//  1. connects to the cast channel and shows the 6-digit pairing code
//     as a small always-visible badge (top-right).
//  2. listens for commands from the phone remote app:
//       - play/stop           -> navigate
//       - pause/seek/vol/...  -> forwarded to the open player via
//                                window.__fwPlayerApi (installed by WatchPlayer)
//       - state               -> publishes live playback state so the phone
//                                remote shows real position/pause status
//
// Desktop / phone browsers render nothing. The badge's refresh button
// rotates the pairing code (fresh MQTT session) via the `nonce` key.
// ---------------------------------------------------------------------------

const TvCastReceiver = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [tvMode] = useState(() => isTvRuntime());
  const [nonce, setNonce] = useState(0); // 0 = first session
  const [handle, setHandle] = useState(null);
  const [status, setStatus] = useState('connecting');
  const [toast, setToast] = useState(null);

  const showToast = useCallback((text) => {
    setToast(text);
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  // One cast session per nonce. The effect below owns start/stop.
  useEffect(() => {
    if (!tvMode) return undefined;

    const onCommand = (msg) => {
      if (msg.type === 'connect') {
        showToast('TV connected from phone');
        navigate('/');
        h.publishState({ playing: false, path: '/', ts: Date.now() });
      } else if (msg.type === 'play' && msg.path && String(msg.path).indexOf('/watch/') === 0) {
        showToast('Now playing: ' + (msg.title || 'video'));
        navigate(msg.path);
      } else if (msg.type === 'stop') {
        showToast('Playback stopped from phone');
        navigate('/');
      } else if (msg.type === 'nav') {
        dispatchNavKey(msg.value, navigate);
      } else if (msg.type === 'pointer') {
        dispatchRemotePointer(msg);
      } else if (msg.type === 'player') {
        if (window.__fwPlayerApi && window.__fwPlayerApi.action) {
          window.__fwPlayerApi.action(msg);
        } else {
          showToast('Player: ' + savePlayerId(msg.value));
        }
      } else if (msg.type === 'state') {
        // Phone remote (re)connected — answer with the current player state.
        const api = window.__fwPlayerApi;
        const state = api && api.state ? api.state() : null;
        h.publishState(state || { playing: false, path: location.pathname });
      } else if (window.__fwPlayerApi && window.__fwPlayerApi.action) {
        // Player open: forward pause/seek/vol/episode/quality/server/source/
        // audio/subtitle straight into the WatchPlayer executor.
        window.__fwPlayerApi.action(msg);
      } else if (msg.type === 'pause' || msg.type === 'resume') {
        showToast(msg.type === 'pause' ? 'Pause requested from phone' : 'Resume requested from phone');
      }
    };

    const h = startCastReceiver(onCommand, setStatus);
    setHandle(h);
    return () => {
      h.stop();
      setHandle(null);
    };
    // location.pathname is read live inside onCommand; keeping it out of the
    // deps avoids tearing down the MQTT session on every navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tvMode, nonce, navigate, showToast]);

  if (!tvMode) return null;

  const code = handle ? handle.code : null;

  const dotColor =
    status === 'online' ? 'text-emerald-400' : status === 'connecting' ? 'text-amber-400' : 'text-red-400';

  return (
    <>
      {/* Persistent pairing badge — top-right */}
      <div className="fixed top-3 right-3 z-[90] flex items-center gap-2 bg-black/70 border border-white/15 rounded-xl px-3 py-2 backdrop-blur-md shadow-xl">
        <FaTv className="text-accent-gold w-4 h-4" />
        <div className="leading-tight">
          <p className="text-[10px] uppercase tracking-widest text-gray-400 font-bold">TV Pair Code</p>
          <p className="text-lg font-black text-white tracking-[0.25em]">{code || '······'}</p>
        </div>
        <div className="flex flex-col items-center gap-1 ml-1">
          <FaCircle className={`${dotColor} w-2.5 h-2.5`} title={status} />
          <button
            onClick={() => setNonce((n) => n + 1)}
            aria-label="New pairing code"
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-gray-300 hover:text-white border border-white/10 transition-colors"
          >
            <FaSyncAlt className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Cast toast */}
      {toast && (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-[95] bg-accent-gold text-dark-900 font-extrabold text-sm sm:text-base px-6 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-fade-in">
          <FaPlay className="w-4 h-4" />
          <span>{toast}</span>
        </div>
      )}
    </>
  );
};

export default TvCastReceiver;
